/**
 * home.jsx - Page d'accueil de HowMuch
 * 
 * Landing page présentant le produit avec :
 * - Hero section avec value proposition claire
 * - Aperçu visuel d'une simulation exemple
 * - Section features expliquant les bénéfices
 * - CTA vers le simulateur
 * 
 * Design :
 * - Dark mode avec palette violet/vert (#7C3AED, #22C55E)
 * - Composants glassmorphism (backdrop-blur)
 * - Animations subtiles avec orbes flottants
 * - Responsive avec media queries
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../seo/useSEO";
import "./home.css";

/**
 * FAQ (contenu SEO + aide utilisateur)
 * - Les réponses sont visibles dans le HTML (meilleur pour SEO qu'un contenu 100% dynamique)
 * - On génère aussi un JSON-LD FAQPage pour aider les moteurs à comprendre Q/R
 */
const FAQ_ITEMS = [
  {
    q: "HowMuch, c’est quoi ?",
    a: "HowMuch est un simulateur de budget long terme : tu saisis un objectif, un montant initial, des mensualités (et un rendement optionnel) et tu vois en combien de temps tu peux atteindre ton objectif.",
  },
  {
    q: "Comment fonctionne le calcul (intérêts composés) ?",
    a: "Chaque mois, l'épargne évolue selon : montant(n+1) = montant(n) × (1 + r) + mensualité, avec r = taux annuel / 12. Le simulateur te donne une timeline claire et un graphe d'évolution.",
  },
  {
    q: "Le simulateur est-il gratuit ?",
    a: "Oui : le simulateur simple est gratuit. La version premium ajoute des scénarios (ex : +50€ de revenu, -50€ de dépenses) et des paliers (25%, 50%, 75%, 100%) pour visualiser l'impact de tes choix.",
  },
  {
    q: "Pourquoi demander un email pour voir les résultats ?",
    a: "L'email sert à débloquer l'affichage des résultats et à t'informer des nouvelles fonctionnalités. Tu peux te désinscrire à tout moment.",
  },
  {
    q: "C’est quoi la règle 50/30/20 et comment l’appliquer ?",
    a: "La règle 50/30/20 est une méthode simple : 50% besoins (logement, factures), 30% envies (loisirs), 20% épargne/dettes. Pour un exemple chiffré et des conseils, consulte l’article dédié.",
    link: { to: "/blog/regle-50-30-20", label: "Lire l’article 50/30/20" },
  },
];

function injectFaqJsonLd() {
  const id = "howmuch-jsonld-faq";
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

/**
 * Composant Home - Page d'accueil (Landing)
 * @returns {JSX.Element} Page d'accueil complète
 */
export default function Home() {
  useSEO({
    title: "HowMuch — Simulateur budget long terme (objectif, mensualités)",
    description:
      "Simule ton budget sur le long terme : objectif, montant mensuel, rendement optionnel. Découvre en combien de temps tu atteins ton objectif.",
    canonicalPath: "/",
    ogType: "website",
  });

  // Injecte le JSON-LD FAQPage (SEO). Le contenu Q/R reste aussi lisible côté UI.
  useEffect(() => {
    injectFaqJsonLd();
  }, []);

  return (
    <main className="hm">
      <header className="hm-header">
        <div className="hm-container hm-header-inner">
          <div className="hm-brand">
            <div className="hm-logo">HM</div>
            <div className="hm-brand-text">
              <div className="hm-brand-name">HowMuch</div>
              <div className="hm-brand-tag">Simulations de budget long terme</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Link to="/blog" className="hm-btn hm-btn-ghost">
              Blog
            </Link>
            <Link to="/simulate" className="hm-btn hm-btn-primary">
              Accéder au simulateur <span className="hm-btn-arrow">→</span>
            </Link>
          </div>
        </div>
        
      </header>

      <section className="hm-hero">
        <div className="hm-container hm-hero-grid">
          <div className="hm-hero-left">
            <div className="hm-pill">
              <span className="hm-pill-dot" />{" Simule ton objectif en 30 secondes"}
            </div>

            <h1 className="hm-title">
              Visualise <span>quand</span> tu atteins ton objectif.
            </h1>

            <p className="hm-subtitle">
              <strong>HowMuch</strong> te donne une projection claire : tu choisis un objectif,
              un montant mensuel (et optionnellement un départ / rendement), et tu sais
              {" "}
              <strong>en combien de temps</strong> tu y arrives.
            </p>

            <div className="hm-hero-cta">
              <Link to="/simulate" className="hm-btn hm-btn-primary hm-btn-lg">
                Lancer une simulation <span className="hm-btn-arrow">→</span>
              </Link>
              <div className="hm-mini-proof">
                ✅ Simple · ⚡ Instantané · 🔒 Privé
              </div>
            </div>

            <div className="hm-stats">
              <div className="hm-stat">
                <div className="hm-stat-value">+ clair</div>
                <div className="hm-stat-label">qu’un tableur</div>
              </div>
              <div className="hm-stat">
                <div className="hm-stat-value">3</div>
                <div className="hm-stat-label">inputs</div>
              </div>
              <div className="hm-stat">
                <div className="hm-stat-value">1</div>
                <div className="hm-stat-label">timeline</div>
              </div>
            </div>
          </div>

          <div className="hm-hero-right">
            <div className="hm-card">
              <div className="hm-card-top">
                <div>
                  <div className="hm-card-title">Aperçu</div>
                  <div className="hm-card-sub">Exemple de simulation</div>
                </div>
                <div className="hm-chip">HowMuch</div>
              </div>

              <div className="hm-card-body">
                <div className="hm-row">
                  <div className="hm-kv">
                    <div className="hm-k">Objectif</div>
                    <div className="hm-v">20 000 €</div>
                  </div>
                  <div className="hm-kv">
                    <div className="hm-k">Mensualités</div>
                    <div className="hm-v">300 €/mois</div>
                  </div>
                </div>

                <div className="hm-highlight">
                  <div className="hm-highlight-title">Résultat</div>
                  <div className="hm-highlight-text">
                    Objectif atteint en <b>5 ans et 7 mois</b>.
                  </div>
                </div>

                <div className="hm-progress">
                  <div className="hm-progress-bar" style={{ width: "64%" }} />
                </div>

                <div className="hm-milestones">
                  <div className="hm-ms">
                    <div className="hm-ms-dot" />
                    <div className="hm-ms-text">
                      <b>25%</b> — 1 an 5 mois
                    </div>
                  </div>
                  <div className="hm-ms">
                    <div className="hm-ms-dot" />
                    <div className="hm-ms-text">
                      <b>50%</b> — 2 ans 10 mois
                    </div>
                  </div>
                  <div className="hm-ms">
                    <div className="hm-ms-dot" />
                    <div className="hm-ms-text">
                      <b>75%</b> — 4 ans 2 mois
                    </div>
                  </div>
                </div>

                <Link to="/simulate" className="hm-btn hm-btn-ghost hm-btn-full">
                  Ouvrir le simulateur <span className="hm-btn-arrow">→</span>
                </Link>

                <div className="hm-card-note">
                  Ajuste les mensualités et vois l’impact instantanément.
                </div>
              </div>
            </div>

            <div className="hm-orb hm-orb-1" />
            <div className="hm-orb hm-orb-2" />
          </div>
        </div>
      </section>

      <section className="hm-section">
        <div className="hm-container">
          <h2 className="hm-h2">Pourquoi HowMuch ?</h2>
          <p className="hm-p">
            Parce qu’un projet long terme, c’est surtout une question de rythme.
            HowMuch te montre le délai et te permet de choisir un plan réaliste.
          </p>

          <div className="hm-features">
            <div className="hm-feature">
              <div className="hm-feature-icon">⏱️</div>
              <div className="hm-feature-title">Temps jusqu’à l’objectif</div>
              <div className="hm-feature-text">
                Une estimation claire à partir de tes mensualités.
              </div>
            </div>
            <div className="hm-feature">
              <div className="hm-feature-icon">🔁</div>
              <div className="hm-feature-title">Teste des scénarios</div>
              <div className="hm-feature-text">
                “Et si je mets +50€ / mois ?” → réponse immédiate.
              </div>
            </div>
            <div className="hm-feature">
              <div className="hm-feature-icon">📈</div>
              <div className="hm-feature-title">Vision long terme</div>
              <div className="hm-feature-text">
                Tu avances avec une timeline, pas avec du flou.
              </div>
            </div>
            <div className="hm-feature">
              <div className="hm-feature-icon">✨</div>
              <div className="hm-feature-title">Simple & clean</div>
              <div className="hm-feature-text">
                Pas de jargon, juste ce qui compte.
              </div>
            </div>
          </div>

          <div className="hm-bottom-cta">
            <Link to="/simulate" className="hm-btn hm-btn-primary hm-btn-lg">
              Commencer maintenant <span className="hm-btn-arrow">→</span>
            </Link>
            <div className="hm-muted">Gratuit · sans engagement</div>
          </div>
        </div>
      </section>

      <section className="hm-section" aria-labelledby="faq-title">
        <div className="hm-container">
          <h2 className="hm-h2" id="faq-title">FAQ — Questions fréquentes</h2>
          <p className="hm-p">
            Réponses rapides sur le simulateur, la méthode de calcul et des règles simples de budget.
          </p>

          <div className="hm-faq" aria-label="FAQ HowMuch">
            {FAQ_ITEMS.map((item) => (
              <details className="hm-faq-item" key={item.q}>
                <summary className="hm-faq-q">{item.q}</summary>
                <div className="hm-faq-a">
                  <p>{item.a}</p>
                  {item.link ? (
                    <Link className="hm-faq-link" to={item.link.to}>
                      {item.link.label} →
                    </Link>
                  ) : null}
                </div>
              </details>
            ))}
          </div>

          <div className="hm-bottom-cta">
            <Link to="/blog" className="hm-btn hm-btn-ghost hm-btn-lg">
              Découvrir le blog <span className="hm-btn-arrow">→</span>
            </Link>
            <Link to="/simulate" className="hm-btn hm-btn-primary hm-btn-lg">
              Faire une simulation <span className="hm-btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      <footer className="hm-footer">
        <div className="hm-container hm-footer-inner">
          <div className="hm-footer-left">
            <div className="hm-footer-brand">
              <span className="hm-footer-logo">HM</span>
              <span>HowMuch</span>
            </div>
            <div className="hm-footer-muted">
              © {new Date().getFullYear()} — Simulations de budget long terme
            </div>
          </div>
          <div className="hm-footer-right">
            <Link to="/blog" className="hm-footer-link">Blog</Link>
            <Link to="/simulate" className="hm-footer-link">Simulateur</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
